import { GetStaticPaths, GetStaticProps, NextPage } from "next";

import Head from "next/head";
import { Header } from "~components/collection/Header";
import { Results } from "~components/collection/Results";
import { FiltersBar } from "~components/search/FiltersBar";
import { Seo } from "../../components";
import { CallToAction } from "../../components/common/Story/blocks/CallToAction";
import { enhancedStaticProps, withLayout } from "../../lib";
import { getCollection } from "../../lib/getCollection";
import { getShopifySdk } from "../../lib/shopify/client";
import { Collection } from "../../lib/shopify/sdk";
import { ContentItem } from "../../lib/storyblok/sdk";

interface CategoryPageProps {
  collection: Collection;
  cta: ContentItem;
  cta2: ContentItem;
  extraCollectionData?: any;
}

const CategoryPage: NextPage<CategoryPageProps> = ({
  collection,
  cta,
  cta2,
  extraCollectionData,
}) => {
  return (
    <>
      <Seo
        title={extraCollectionData?.seo?.title || collection.title}
        description={
          extraCollectionData?.seo?.description || collection.description
        }
        image={collection.image?.originalSrc}
        canonical={`https://www.naturalheroes.nl/collections/${collection.handle}`}
      />

      <Header
        extraCollectionData={extraCollectionData}
        collection={collection}
      />
      <Results />

      {cta2 && <CallToAction content={cta2?.content} mb={8} />}
    </>
  );
};

export default withLayout(CategoryPage);

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    fallback: "blocking",
    paths: [],
  };
};

export const getStaticProps: GetStaticProps<CategoryPageProps> =
  enhancedStaticProps(async ({ params, sdk }) => {
    const { handle } = params;
    const shopifySdk = getShopifySdk();

    let collection = null;
    let extraCollectionData = null;
    let notFound = false;

    try {
      collection = (
        await shopifySdk.collection({ handle: handle as string, first: 0 })
      ).collectionByHandle;
    } catch (err) {
      notFound = true;
    }

    try {
      extraCollectionData = await getCollection(handle as string).then(
        (res) => res.collectionByHandle
      );
      if (extraCollectionData?.heroImage?.value) {
        const heroImage = JSON.parse(extraCollectionData?.heroImage?.value);

        extraCollectionData.heroImage = {
          alt: heroImage?.[0]?.alt || "",
          src: heroImage?.[0]?.src || "",
        };
      }
    } catch (err) {}

    const [{ ContentNode: cta }, { ContentNode: cta2 }] = await Promise.all([
      sdk.callToAction({ slug: "/settings/cta-s/call-to-action" }),
      sdk.callToAction({ slug: "/settings/cta-s/call-to-action-2" }),
    ]);
    return {
      props: {
        collection: collection || null,
        extraCollectionData: extraCollectionData || null,
        cta,
        cta2,
      },
      notFound: !collection || notFound,
      revalidate: process.env.HOST === "dev.naturalheroes.nl" ? 1 : 3600,
    };
  });
